import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { reachGoal } from "@/lib/metrika";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const MAX_CHANNEL_URL = "https://max.ru/id500122938292_biz";

interface MaxChannelButtonProps {
  variant?: "fab" | "inline";
  className?: string;
}

export function MaxChannelButton({ variant = "fab", className }: MaxChannelButtonProps) {
  const isFab = variant === "fab";
  const size = isFab ? "h-14 w-14" : "h-10 w-10";
  const letterSize = isFab ? "text-xl" : "text-base";

  const handleClick = () => {
    reachGoal("max_channel_click", { variant });
  };

  const button = (
    <motion.a
      href={MAX_CHANNEL_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      aria-label="Перейти в канал ERIDON в MAX"
      initial={isFab ? { scale: 0, opacity: 0 } : false}
      animate={isFab ? { scale: 1, opacity: 1 } : undefined}
      transition={{ type: "spring", stiffness: 260, damping: 20, delay: isFab ? 0.6 : 0 }}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.95 }}
      className={cn(
        "relative inline-flex items-center justify-center rounded-full",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088FF] focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        size,
        className
      )}
    >
      {/* Pulse rings */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(0,136,255,0.5) 0%, rgba(0,136,255,0) 70%)",
          animation: "max-pulse-ring 2s ease-out infinite",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(124,58,237,0.45) 0%, rgba(124,58,237,0) 70%)",
          animation: "max-pulse-ring 2s ease-out infinite",
          animationDelay: "1s",
        }}
      />

      {/* Glass body with gradient border */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full p-[1.5px]"
        style={{
          background: "linear-gradient(135deg, #0088FF 0%, #4FB3FF 50%, hsl(262 83% 58%) 100%)",
        }}
      >
        <span
          className="block h-full w-full rounded-full"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,136,255,0.85) 0%, rgba(0,102,204,0.9) 60%, rgba(124,58,237,0.85) 100%)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            boxShadow:
              "0 8px 24px -6px rgba(0,136,255,0.55), inset 0 1px 0 0 rgba(255,255,255,0.25)",
          }}
        />
      </span>

      {/* Letter M */}
      <span
        className={cn(
          "relative z-10 font-extrabold tracking-tight text-white",
          letterSize
        )}
        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}
      >
        M
      </span>

      {/* FAB caption (desktop only) — decorative, hidden from a11y tree to match aria-label */}
      {isFab && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-2 -right-2 z-20 hidden md:inline-flex items-center rounded-full bg-[#003D80] px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-lg"
          style={{ boxShadow: "0 4px 12px -2px rgba(0,61,128,0.7)" }}
        >
          Канал
        </span>
      )}
    </motion.a>
  );

  if (isFab) {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="fixed z-40 right-4 md:right-6 bottom-[calc(env(safe-area-inset-bottom,0px)+5.5rem)] md:bottom-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]"
            >
              {button}
            </div>
          </TooltipTrigger>
          <TooltipContent side="left" className="font-medium">
            Подписаться на канал MAX
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom" className="font-medium">
          Канал ERIDON в MAX
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
